/**
 * GMDSS Matrix Logic - WBC3 Table 17.2.1 Compliant
 * Implements the category×sea-area matrix system with conditional logic
 */

import { parseCategory } from '../../../../../../utils/categoryHelpers';

// GMDSS Sea Areas
export const GMDSS_SEA_AREAS = {
  'A1': 'Sea Area A1 - VHF coverage from shore (coastal waters)',
  'A1+A2': 'Sea Areas A1+A2 - MF coverage (up to ~150nm from coast)', 
  'A1+A2+A3': 'Sea Areas A1+A2+A3 - Satellite coverage (global except polar)',
  'A1+A2+A3+A4': 'Sea Areas A1+A2+A3+A4 - Global including polar regions'
};

// Voyage Duration Options
export const VOYAGE_DURATION_OPTIONS = {
  'under_12h': 'Typical voyages under 12 hours',
  'over_12h': 'Voyages over 12 hours', 
  'mixed': 'Mixed - some voyages over 12 hours'
};

// Visual Alerting Effectiveness Options
export const VISUAL_ALERTING_OPTIONS = {
  'yes': 'Visual/VHF alerting to shore is reliable',
  'no': 'May operate in areas where visual/VHF alerting is ineffective',
  'uncertain': 'Uncertain - recommend EPIRB for safety'
};

// Weather Info Availability Options  
export const WEATHER_INFO_OPTIONS = {
  'available': 'Reliable weather/navigation info available by other means',
  'not_available': 'No other reliable source of weather/navigation info',
  'uncertain': 'Uncertain - recommend NAVTEX for safety'
};

// UK Waters Operation Options
export const UK_WATERS_OPTIONS = {
  'yes': 'Operates in UK waters',
  'no': 'Does not operate in UK waters',
  'sometimes': 'Sometimes operates in UK waters'
};

/**
 * Resolve GMDSS requirements based on WBC3 Table 17.2.1 matrix
 */
export const resolveGMDSSRequirements = (vesselConfig) => {
  // Use parseCategory to handle all formats: 'cat3', '3', 3, 'CAT3', etc.
  const category = parseCategory(vesselConfig.category);
  
  const seaArea = vesselConfig.gmdssSeaArea;
  const vesselType = vesselConfig.vesselType;

  if (!seaArea) {
    return {
      requirements: [],
      needsSeaAreaSelection: true
    };
  }

  const requirements = [];

  // VHF Radio with DSC - Universal requirement for ALL vessels
  requirements.push({
    equipmentId: 'vhf_radio_dsc',
    name: 'VHF Radio with DSC',
    status: 'MANDATORY',
    reason: 'Universal requirement for all commercial vessels',
    reference: 'WBC3 Table 17.2.1 - All categories/areas',
    quantity: '1',
    category: 'communication'
  });

  // AIS - Cat 0-2 only (WBC3 Section 19.7.1.4)  
  if (category >= 0 && category <= 2) {
    requirements.push({
      equipmentId: 'ais',
      name: 'AIS (Automatic Identification System)',
      status: 'MANDATORY',
      reason: `Category ${category} requirement (WBC3 Section 19.7.1.4)`,
      reference: 'WBC3 Section 19.7.1.4',
      quantity: '1',
      category: 'navigation'
    });
  }

  // GPS - Cat 0-2 only (WBC3 Section 19.7.1.1) - FIXED: Was incorrectly including Cat 3-4
  if (category >= 0 && category <= 2) {
    requirements.push({
      equipmentId: 'gps',
      name: 'GPS Navigation System',
      status: 'MANDATORY', 
      reason: `Category ${category} requirement - electronic position fixing system (WBC3 Section 19.7.1.1)`,
      reference: 'WBC3 Section 19.7.1.1',
      quantity: '2', // Dual GPS for Cat 0-2
      category: 'navigation'
    });
  }

  // Radar - Cat 0-2 only (WBC3 Section 19.7.1.3) - FIXED: Corrected reference and specification
  if (category >= 0 && category <= 2) {
    requirements.push({
      equipmentId: 'radar',
      name: '3cm Radar System (MIN 698 Standard)',
      status: 'MANDATORY',
      reason: `Category ${category} requirement - 3cm radar on appropriate standard (WBC3 Section 19.7.1.3)`,
      reference: 'WBC3 Section 19.7.1.3',
      quantity: '1',
      category: 'navigation'
    });
  }

  // Echo Sounder - ALL vessels (WBC3 Section 19.5.1)
  requirements.push({
    equipmentId: 'echo_sounder',
    name: 'Echo Sounder',
    status: 'MANDATORY',
    reason: 'Required for all commercial vessels (WBC3 Section 19.5.1)',
    reference: 'WBC3 Section 19.5.1', 
    quantity: '1',
    category: 'navigation'
  });

  // EPIRB - Complex matrix logic
  const epirb = resolveEPIRBRequirement(category, seaArea, vesselConfig);
  if (epirb) {
    requirements.push(epirb);
  }

  // NAVTEX - Complex conditional logic
  const navtex = resolveNAVTEXRequirement(category, seaArea, vesselConfig);
  if (navtex) {
    requirements.push(navtex);
  }

  // MF/HF Radio or Satellite alternatives
  const mfAlternatives = resolveMFAlternatives(category, seaArea, vesselConfig);
  requirements.push(...mfAlternatives);

  // PLB (Personal Locator Beacon) requirements
  const plb = resolvePLBRequirement(category, seaArea, vesselConfig);
  if (plb) {
    requirements.push(plb);
  }

  // Police boat special requirements (Annex 3)
  if (vesselType === 'police') {
    const policeReqs = resolvePoliceBoatRequirements(category, seaArea, vesselConfig);
    requirements.push(...policeReqs);
  }

  // International voyages enhanced requirements
  if (vesselConfig.internationalVoyages) {
    const intlReqs = resolveInternationalVoyageRequirements(category, seaArea, vesselConfig);
    requirements.push(...intlReqs);
  }

  return {
    requirements,
    needsSeaAreaSelection: false,
    conditionalQuestions: getConditionalQuestions(requirements, vesselConfig)
  };
};

/**
 * EPIRB requirement resolution based on WBC3 Table 17.2.1
 */
const resolveEPIRBRequirement = (category, seaArea, vesselConfig) => {
  // Categories 0-4: Always required for A2+ areas
  if (category >= 0 && category <= 4 && seaArea !== 'A1') {
    return {
      equipmentId: 'epirb',
      name: 'EPIRB (406MHz with GPS)',
      status: 'MANDATORY',
      reason: `Category ${category} in ${seaArea} requirement`,
      reference: 'WBC3 Table 17.2.1',
      quantity: '1',
      category: 'communication'
    };
  }

  // Category 5: Always required (all sea areas)
  if (category === 5) {
    return {
      equipmentId: 'epirb', 
      name: 'EPIRB (406MHz with GPS)',
      status: 'MANDATORY',
      reason: 'Category 5 requirement for all sea areas',
      reference: 'WBC3 Table 17.2.1',
      quantity: '1',
      category: 'communication'
    };
  }

  // Category 6: Conditional based on Note A
  if (category === 6) {
    const visualAlerting = vesselConfig.visualAlertingEffective;
    
    if (visualAlerting === 'no' || visualAlerting === 'uncertain') {
      return {
        equipmentId: 'epirb',
        name: 'EPIRB (406MHz with GPS)', 
        status: 'MANDATORY',
        reason: 'Category 6: Required when visual/VHF alerting is ineffective (Note A)',
        reference: 'WBC3 Table 17.2.1 Note A',
        quantity: '1',
        category: 'communication'
      };
    }
    
    if (visualAlerting === 'yes') {
      return {
        equipmentId: 'epirb',
        name: 'EPIRB (406MHz with GPS)',
        status: 'RECOMMENDED',
        reason: 'Category 6: Not required but recommended for safety',
        reference: 'WBC3 Table 17.2.1 Note A',
        quantity: '1',
        category: 'communication'
      };
    }
  }

  return null;
};

/**
 * NAVTEX requirement resolution with complex conditional logic
 */
const resolveNAVTEXRequirement = (category, seaArea, vesselConfig) => {
  // Categories 0-4 in A2+ areas: Always required
  if (category >= 0 && category <= 4 && seaArea !== 'A1') {
    return {
      equipmentId: 'navtex',
      name: 'NAVTEX Receiver', 
      status: 'MANDATORY',
      reason: `Category ${category} in ${seaArea} requirement`,
      reference: 'WBC3 Table 17.2.1',
      quantity: '1',
      category: 'navigation'
    };
  }

  // Categories 5-6: Conditional based on Note B/C
  if (category >= 5) {
    const voyageDuration = vesselConfig.voyageDuration;
    const weatherInfo = vesselConfig.alternativeWeatherInfo;

    // Note B: Required if voyages >12h and no other weather info
    if ((voyageDuration === 'over_12h' || voyageDuration === 'mixed') && 
        (weatherInfo === 'not_available' || weatherInfo === 'uncertain')) {
      return {
        equipmentId: 'navtex',
        name: 'NAVTEX Receiver',
        status: 'MANDATORY', 
        reason: `Category ${category}: Required for voyages >12h without other weather info (Note B)`,
        reference: 'WBC3 Table 17.2.1 Note B',
        quantity: '1',
        category: 'navigation'
      };
    }

    // Recommended in other cases
    return {
      equipmentId: 'navtex',
      name: 'NAVTEX Receiver',
      status: 'RECOMMENDED',
      reason: `Category ${category}: Recommended for weather/navigation warnings`,
      reference: 'WBC3 Table 17.2.1 Note C',
      quantity: '1', 
      category: 'navigation'
    };
  }

  return null;
};

/**
 * MF/HF Radio vs Satellite alternatives resolution
 */
const resolveMFAlternatives = (category, seaArea, vesselConfig) => {
  const alternatives = [];

  // Categories 0-4 in A2+ areas need MF OR Satellite
  if (category >= 0 && category <= 4 && seaArea !== 'A1') {
    // Option 1: MF/HF Radio
    alternatives.push({
      equipmentId: 'mf_hf_radio',
      name: 'MF/HF Radio with DSC',
      status: 'ALTERNATIVE_A',
      reason: `Category ${category} in ${seaArea}: MF/HF OR Satellite required`,
      reference: 'WBC3 Table 17.2.1',
      quantity: '1',
      category: 'communication',
      alternativeGroup: 'mf_satellite'
    });

    // Option 2: Satellite Service  
    alternatives.push({
      equipmentId: 'satellite_service',
      name: 'Satellite Communication Service',
      status: 'ALTERNATIVE_B', 
      reason: `Category ${category} in ${seaArea}: MF/HF OR Satellite required`,
      reference: 'WBC3 Table 17.2.1',
      quantity: '1',
      category: 'communication',
      alternativeGroup: 'mf_satellite'
    });
  }

  // UK Waters conditional logic (Note C)
  if (vesselConfig.operatesUKWaters === 'yes' || vesselConfig.operatesUKWaters === 'sometimes') {
    // Add UK-specific MF requirements
  }

  return alternatives;
};

/**
 * PLB requirement resolution  
 */
const resolvePLBRequirement = (category, seaArea, vesselConfig) => {
  // Categories 0-4: Mandatory 
  if (category >= 0 && category <= 4) {
    return {
      equipmentId: 'plb',
      name: 'PLB (Personal Locator Beacon)',
      status: 'MANDATORY',
      reason: `Category ${category} requirement for personal safety`,
      reference: 'WBC3 Table 17.2.1',
      quantity: 'crew_count', // One per crew member
      category: 'safety'
    };
  }

  // Category 5 in A1: Recommended only
  if (category === 5 && seaArea === 'A1') {
    return {
      equipmentId: 'plb',
      name: 'PLB (Personal Locator Beacon)', 
      status: 'RECOMMENDED',
      reason: 'Category 5 in A1: Recommended for personal safety',
      reference: 'WBC3 Table 17.2.1',
      quantity: 'crew_count',
      category: 'safety'
    };
  }

  return null;
};

/**
 * Police boat special requirements (Annex 3)
 */
const resolvePoliceBoatRequirements = (category, seaArea, vesselConfig) => {
  const requirements = [];

  // Police boats have special GMDSS variations per Annex 3
  // Add specific police requirements here based on Annex 3 tables

  return requirements;
};

/**
 * International voyage enhanced requirements (SOLAS integration)
 */
const resolveInternationalVoyageRequirements = (category, seaArea, vesselConfig) => {
  const requirements = [];

  // Enhanced GMDSS for international voyages
  if (seaArea !== 'A1') {
    requirements.push({
      equipmentId: 'enhanced_gmdss_suite',
      name: 'Enhanced GMDSS Suite (International)',
      status: 'MANDATORY',
      reason: 'International voyages require enhanced GMDSS compliance per SOLAS',
      reference: 'SOLAS Chapter IV + WBC3',
      quantity: '1',
      category: 'communication'
    });
  }

  // Satellite communication for extended operations
  requirements.push({
    equipmentId: 'sat_comms_backup',
    name: 'Backup Satellite Communication',
    status: 'RECOMMENDED',
    reason: 'International voyages: Backup communication recommended',
    reference: 'International best practice',
    quantity: '1', 
    category: 'communication'
  });

  // Enhanced navigation equipment for international waters
  if (category >= 0 && category <= 3) {
    requirements.push({
      equipmentId: 'enhanced_navigation_suite',
      name: 'Enhanced Navigation Suite (International)',
      status: 'MANDATORY',
      reason: 'International voyages require enhanced navigation capability',
      reference: 'SOLAS Chapter V + WBC3',
      quantity: '1',
      category: 'navigation'
    });
  }

  return requirements;
};

/**
 * Get conditional questions that need to be asked based on equipment
 */
const getConditionalQuestions = (requirements, vesselConfig) => {
  const questions = [];

  // Check if EPIRB conditional logic needs visual alerting question
  const hasConditionalEPIRB = requirements.find(r => 
    r.equipmentId === 'epirb' && r.reference?.includes('Note A')
  );
  if (hasConditionalEPIRB && !vesselConfig.visualAlertingEffective) {
    questions.push('visualAlertingEffective');
  }

  // Check if NAVTEX conditional logic needs voyage duration question
  const hasConditionalNAVTEX = requirements.find(r =>
    r.equipmentId === 'navtex' && r.reference?.includes('Note B')
  );
  if (hasConditionalNAVTEX && !vesselConfig.voyageDuration) {
    questions.push('voyageDuration');
  }

  // Check if weather info question needed
  if (hasConditionalNAVTEX && !vesselConfig.alternativeWeatherInfo) {
    questions.push('alternativeWeatherInfo');
  }

  // Check if UK waters question needed
  const hasMFRequirement = requirements.find(r =>
    r.equipmentId === 'mf_hf_radio' && !vesselConfig.operatesUKWaters
  );
  if (hasMFRequirement) {
    questions.push('operatesUKWaters');
  }

  return questions;
};